import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Radio,
  Button,
  DatePicker,
  Input,
  message,
  Alert,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Tag,
  Breadcrumb
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  TagOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  HeartOutlined,
  EyeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import EmployeeSelect from '../components/common/EmployeeSelect';
import AttachmentManager from '../components/sop/AttachmentManager';
import MessagePreview from '../components/sop/MessagePreview';
import TagSelect from '../components/common/TagSelect';
import massSendService from '../services/massSendService';
import api from '../services/api';
import useMediaState from '../hooks/useMediaState';
import { useFixedFooter, useFixedFooterPlaceholder } from '../hooks/useFixedFooter';

const { Title, Text } = Typography;
 

const MomentMassSend = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  

  // 监听表单变化用于实时预览
  const [formValues, setFormValues] = useState({});

  // 直接管理attachments状态，模仿StepModal的做法
  const [attachments, setAttachments] = useState([]);

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 判断页面模式：view, copy, edit, create
  const getPageMode = () => {
    const path = location.pathname;
    if (path.includes('/view/')) return 'view';
    if (path.includes('/edit/')) return 'edit';
    if (location.state?.copyFrom) return 'copy';
    return 'create';
  };

  const pageMode = getPageMode();
  const isViewMode = pageMode === 'view';
  const isCopyMode = pageMode === 'copy';
  const isEditMode = pageMode === 'edit';

  // 使用媒体状态管理Hook
  const {
    mediaState,
    setMediaState,
    updateMediaId,
    updateSpecificMediaId,
    updateOssUrl,
    updateImageGroup,
    clearMedia,
    removeFromImageGroup
  } = useMediaState();

  const [previewUrls, setPreviewUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // 不再本地加载标签，交由通用组件内部处理

  // 加载任务数据（查看和编辑模式）
  useEffect(() => {
    const loadTaskData = async () => {
      if (taskId && (isViewMode || isEditMode)) {
        try {
          setLoading(true);
          const response = await massSendService.getTaskById(taskId);
          if (response.success && response.data) {
            const taskData = response.data;
            fillFormWithTaskData(taskData);
          } else {
            message.error('任务不存在');
            navigate('/moment-mass-send');
          }
        } catch (error) {
          console.error('加载任务数据失败:', error);
          message.error('加载任务数据失败');
          navigate('/moment-mass-send');
        } finally {
          setLoading(false);
        }
      }
    };

    loadTaskData();
  }, [taskId, isViewMode, isEditMode, navigate]);

  // 处理复制模式的数据填充
  useEffect(() => {
    if (isCopyMode && location.state?.copyFrom) {
      const taskData = location.state.copyFrom;
      console.log('🔄 朋友圈复制模式 - 接收到的任务数据:', taskData);
      fillFormWithTaskData(taskData);
    }
  }, [isCopyMode, location.state]);

  // 处理 AI 生成内容自动填入
  useEffect(() => {
    if (location.state?.aiGeneratedText && !isCopyMode && !isViewMode && !isEditMode) {
      const text = location.state.aiGeneratedText;
      const images = location.state.aiGeneratedImages || [];
      const newAttachments = [{ id: `text-${Date.now()}`, type: 'MOMENT_TEXT', content: text }];

      if (images.length > 0) {
        const ossUrls = {};
        images.forEach((img, i) => {
          ossUrls[i] = img.ossUrl || img.url;
        });
        newAttachments.push({
          id: `img-${Date.now()}`,
          type: 'MOMENT_IMAGE_GROUP',
          imageGroup: { mediaIds: [], ossUrls },
        });
      }

      setAttachments(newAttachments);
      message.info('已从 AI 生成内容填入');
    }
  }, [location.state]);

  // 填充表单数据
  const fillFormWithTaskData = (taskData) => {
    try {
      console.log('📋 朋友圈开始填充表单数据:', taskData);
      console.log('🔍 朋友圈任务数据所有字段:');
      Object.keys(taskData).forEach(key => {
        console.log(`  ${key}:`, taskData[key], `(${typeof taskData[key]})`);
      });
      // 解析附件数据
      let parsedAttachments = [];
      if (taskData.attachments) {
        try {
          const attachmentData = typeof taskData.attachments === 'string'
            ? JSON.parse(taskData.attachments)
            : taskData.attachments;

          if (Array.isArray(attachmentData)) {
            parsedAttachments = attachmentData;
          }
        } catch (e) {
          console.error('解析附件数据失败:', e);
        }
      }

      // 如果有文本内容但没有MOMENT_TEXT类型附件，添加一个
      if (taskData.textContent && !parsedAttachments.find(att => att.type === 'MOMENT_TEXT')) {
        parsedAttachments.unshift({
          id: `moment_text-${Date.now()}`,
          type: 'MOMENT_TEXT',
          content: taskData.textContent
        });
      }

      // 如果没有任何附件，添加默认的MOMENT_TEXT附件
      if (parsedAttachments.length === 0) {
        parsedAttachments = [{
          id: `moment_text-${Date.now()}`,
          type: 'MOMENT_TEXT',
          content: taskData.textContent || ''
        }];
      }

      setAttachments(parsedAttachments);

      // 解析目标配置
      let targetConfigObj = null;
      if (taskData.targetConfig) {
        try {
          targetConfigObj = typeof taskData.targetConfig === 'string'
            ? JSON.parse(taskData.targetConfig)
            : taskData.targetConfig;
        } catch (e) {
          console.error('解析目标配置失败:', e);
        }
      }

      // 填充表单
      const formData = {
        taskName: taskData.taskName,
        members: (() => {
          // 尝试多种可能的成员字段名
          const memberData = taskData.members || taskData.memberList || taskData.employees || taskData.users || taskData.executors;
          console.log('🔍 朋友圈尝试提取成员数据 - memberData:', memberData);

          if (!memberData || !Array.isArray(memberData)) {
            return [];
          }

          return memberData.map(m => {
            // 优先使用 userId，然后是 id，最后是 memberId，并转换为字符串
            const memberId = m.userId || m.id || m.memberId || m.employeeId || m.weworkUserId;
            return memberId ? String(memberId) : null;
          }).filter(Boolean);
        })(),
        targetTags: targetConfigObj?.tagIds || [],
        scheduleType: taskData.sendTimeType || 'IMMEDIATE',
        scheduleDateTime: taskData.scheduledTime ? dayjs(taskData.scheduledTime) : null
      };

      console.log('📝 朋友圈准备填充的表单数据:', formData);
      console.log('📎 朋友圈解析后的附件数据:', parsedAttachments);
      console.log('🎯 朋友圈目标配置对象:', targetConfigObj);
      console.log('⏰ 朋友圈原始时间数据:', taskData.scheduledTime);
      console.log('⏰ 朋友圈解析后时间数据:', formData.scheduleDateTime);
      // 检查所有可能的成员字段
      console.log('👥 朋友圈检查成员相关字段:');
      console.log('  - taskData.members:', taskData.members);
      console.log('  - taskData.memberList:', taskData.memberList);
      console.log('  - taskData.employees:', taskData.employees);
      console.log('  - taskData.users:', taskData.users);
      console.log('  - taskData.executors:', taskData.executors);

      const memberData = taskData.members || taskData.memberList || taskData.employees || taskData.users || taskData.executors;
      console.log('👥 朋友圈选中的成员数据源:', memberData);
      console.log('👥 朋友圈成员数据详细信息:');
      if (memberData && Array.isArray(memberData)) {
        memberData.forEach((member, index) => {
          console.log(`  成员${index + 1}:`, member);
          console.log(`    - userId: ${member.userId} (${typeof member.userId})`);
          console.log(`    - memberId: ${member.memberId} (${typeof member.memberId})`);
          console.log(`    - id: ${member.id} (${typeof member.id})`);
          console.log(`    - employeeId: ${member.employeeId} (${typeof member.employeeId})`);
          console.log(`    - weworkUserId: ${member.weworkUserId} (${typeof member.weworkUserId})`);
          const selectedId = member.userId || member.id || member.memberId || member.employeeId || member.weworkUserId;
          console.log(`    - 选择的ID: ${selectedId} (${typeof selectedId}) -> ${String(selectedId)}`);
        });
      }
      console.log('🎯 朋友圈提取的成员ID列表:', formData.members);
      console.log('🎯 朋友圈成员ID类型检查:', formData.members.map(id => `${id}(${typeof id})`));

      form.setFieldsValue(formData);
      setFormValues(formData);
    } catch (error) {
      console.error('填充表单数据失败:', error);
      message.error('填充表单数据失败');
    }
  };

  // 监听表单值变化
  const handleValuesChange = (changedValues, allValues) => {
    // 同步表单值到状态（除了attachments，因为attachments由单独状态管理）
    const { attachments: formAttachments, ...otherValues } = allValues;
    setFormValues({ ...otherValues, attachments });
  };

  // 初始化默认附件
  const initializeAttachments = () => {
    return []; // 返回空数组，让AttachmentManager内部的useEffect自动添加默认的MOMENT_TEXT附件
  };

  // 初始化attachments状态
  useEffect(() => {
    if (attachments.length === 0) {
      // 复制模式下不要用默认内容覆盖从列表页带过来的内容
      if (location.state?.copyFrom) {
        return;
      }
      const defaultAttachments = [
        {
          id: `moment_text-${Date.now()}`,
          type: 'MOMENT_TEXT',
          content: ''
        }
      ];
      setAttachments(defaultAttachments);
    }
  }, []);

  // 文件上传处理
  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError, onProgress, fieldKey, mediaType, stepType } = options;

    try {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: true }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

      // 获取租户ID
      const getTenantId = () => {
        try {
          const tenantStr = localStorage.getItem('tenant');
          if (tenantStr) {
            const tenant = JSON.parse(tenantStr);
            return tenant.id;
          }
        } catch (error) {
          console.error('获取租户ID失败:', error);
        }
        return null;
      };

      const tenantId = getTenantId();
      if (!tenantId) {
        throw new Error('无法获取租户信息');
      }

      // 根据文件类型确定mediaType
      let actualMediaType = 'image'; // 默认为图片
      if (file.type.startsWith('video/')) {
        actualMediaType = 'video';
      } else if (file.type.startsWith('image/')) {
        actualMediaType = 'image';
      } else {
        actualMediaType = 'file';
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('mediaType', actualMediaType);
      formData.append('tenantId', tenantId);
      formData.append('attachmentType', '1'); // 朋友圈附件类型
      formData.append('sceneType', 'moment'); // 朋友圈使用moment场景

      // 朋友圈使用附件上传接口
      const response = await api.post('/media/wework/upload_attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [uploadKey]: progress }));
          if (onProgress) onProgress({ percent: progress });
        }
      });

      if (response.data.success) {
        const { mediaId, ossUrl } = response.data;

        // 更新预览URL
        setPreviewUrls(prev => ({ ...prev, [mediaId]: ossUrl }));

        // 调用成功回调
        if (onSuccess) {
          onSuccess({ mediaId, ossUrl, success: true });
        }
      } else {
        throw new Error(response.data.message || '上传失败');
      }
    } catch (error) {
      console.error('文件上传失败:', error);
      if (onError) onError(error);
      message.error('文件上传失败');
    } finally {
      const uploadKey = `${stepType}_${fieldKey}`;
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));
    }
  };

  // 文件删除处理
  const handleRemoveFile = (fieldKey, stepType, mediaIdToRemove = null) => {
    // 处理图片组的特殊删除逻辑
    if (mediaIdToRemove) {
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[mediaIdToRemove];
        return newUrls;
      });
    } else {
      // 普通文件删除
      setPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[fieldKey];
        return newUrls;
      });
    }
  };

  // URL验证函数
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // 获取表单初始值
  const getInitialValues = () => {
    return {
      scheduleType: 'IMMEDIATE',
      attachments: initializeAttachments()
    };
  };

  // 提取文本内容（朋友圈专用）
  const extractTextContent = (attachments) => {
    const textAttachment = attachments.find(att => att.type === 'MOMENT_TEXT');
    return textAttachment ? textAttachment.content || '' : '';
  };

  // 提取非文本附件
  const extractNonTextAttachments = (attachments) => {
    return attachments.filter(att => att.type !== 'MOMENT_TEXT');
  };

  // 提交任务
  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      // 提取文本内容和非文本附件
      const textContent = extractTextContent(attachments);
      const nonTextAttachments = extractNonTextAttachments(attachments);

      // 手动验证内容
      const hasTextContent = textContent && textContent.trim().length > 0;
      const hasNonTextAttachments = nonTextAttachments.length > 0;
      const hasValidAttachments = nonTextAttachments.some(att => {
        // 检查各种类型的附件是否有有效内容
        if (att.type === 'MOMENT_IMAGE_GROUP') {
          return att.imageGroup && att.imageGroup.mediaIds && att.imageGroup.mediaIds.length > 0;
        }
        if (att.type === 'MOMENT_VIDEO') {
          return att.mediaId && att.mediaId.trim().length > 0;
        }
        if (att.type === 'MOMENT_LINK') {
          // 朋友圈链接必须有URL和封面图
          return att.url && att.url.trim().length > 0 && att.picMaterialId;
        }
        // 朋友圈不支持小程序
        return false;
      });

      console.log('内容验证调试:', {
        textContent,
        hasTextContent,
        nonTextAttachments,
        hasNonTextAttachments,
        hasValidAttachments,
        attachments
      });

      if (!hasTextContent && !hasValidAttachments) {
        message.error('请添加朋友圈内容');
        setSubmitLoading(false);
        return;
      }

      // 额外验证：朋友圈链接必须有封面图
      const invalidLinks = nonTextAttachments.filter(att =>
        att.type === 'MOMENT_LINK' && (!att.picMaterialId || !att.picUrl_ossUrl)
      );
      if (invalidLinks.length > 0) {
        message.error('朋友圈链接必须选择封面图片');
        setSubmitLoading(false);
        return;
      }

      const values = await form.validateFields();

      // 构建目标配置 - 根据是否选择了标签来决定类型
      const hasSelectedTags = values.targetTags && values.targetTags.length > 0;
      const targetType = hasSelectedTags ? 'TAG' : 'ALL';

      // 构建任务数据
      const taskData = {
        taskName: values.taskName,
        taskType: 'MOMENT',
        sendTimeType: values.scheduleType,
        scheduledTime: values.scheduleType === 'SCHEDULED' ? values.scheduleDateTime?.format('YYYY-MM-DD HH:mm:ss') : null,
        textContent: textContent,
        attachments: nonTextAttachments.length > 0 ? JSON.stringify(nonTextAttachments) : null,
        targetType: targetType,
        targetConfig: JSON.stringify({
          targetType: targetType,
          tagIds: hasSelectedTags ? values.targetTags : null
        }),
        members: values.members.map(memberId => ({ userId: memberId }))
      };

      // 提交任务
      const result = await massSendService.createTask(taskData);

      if (values.scheduleType === 'IMMEDIATE') {
        // 立即执行
        await massSendService.executeTask(result.data.id);
        message.success('朋友圈群发任务已提交并开始执行');
      } else {
        message.success('朋友圈群发任务已创建，将在指定时间执行');
      }

      // 跳转回列表页
      navigate('/moment-mass-send');

    } catch (error) {
      console.error('提交任务失败:', error);
      message.error(error.response?.data?.message || '提交任务失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 重置表单
  const handleReset = () => {
    form.resetFields();
    setFormValues({});

    // 重置attachments状态
    const defaultAttachments = [
      {
        id: `moment_text-${Date.now()}`,
        type: 'MOMENT_TEXT',
        content: ''
      }
    ];
    setAttachments(defaultAttachments);
  };

  // 获取页面标题
  const getPageTitle = () => {
    switch (pageMode) {
      case 'view': return '查看朋友圈群发';
      case 'copy': return '复制朋友圈群发';
      default: return '创建朋友圈群发';
    }
  };

  // 返回列表页
  const handleGoBack = () => {
    navigate('/moment-mass-send');
  };

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item onClick={() => navigate('/moment-mass-send')}>
          <Title level={5} style={{ cursor: 'pointer', color: '#1677ff' }}>朋友圈群发</Title>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Title level={5}>{getPageTitle()}</Title>
        </Breadcrumb.Item>
      </Breadcrumb>
      {isViewMode && (
        <Tag icon={<EyeOutlined />} color="blue">
          只读模式
        </Tag>
      )}

      <Row gutter={24}>
        {/* 左侧配置区域 */}
        <Col span={14}>
          <Card title="朋友圈设置" loading={loading}>
            <Form
              form={form}
              layout="vertical"
              onValuesChange={handleValuesChange}
              initialValues={getInitialValues()}
            >
              {/* 朋友圈名称 */}
              <Form.Item
                name="taskName"
                label="朋友圈名称"
                rules={[{ required: true, message: '请输入朋友圈名称' }]}
              >
                <Input
                  placeholder="请输入朋友圈任务名称"
                  disabled={isViewMode}
                />
              </Form.Item>

              {/* 发送成员 */}
              <Form.Item
                name="members"
                label={<>发送成员</>}
                rules={[{ required: true, message: '请至少选择一个发送成员' }]}
              >
                <EmployeeSelect
                  multiple
                  placeholder="请选择或搜索发送成员（可多选）"
                  disabled={isViewMode}
                />
              </Form.Item>

              {/* 可见范围 */}
              <Form.Item
                name="targetTags"
                label={<>可见范围</>}
                help="选择标签后，朋友圈只对拥有这些标签的客户可见（不选择则对所有客户可见）"
              >
                <TagSelect
                  mode="multiple"
                  placeholder="请选择客户标签（可选）"
                  disabled={isViewMode}
                />
              </Form.Item>

              <Divider />

              {/* 朋友圈内容 */}
              <Form.Item
                name="attachments"
                label={<>朋友圈内容</>}
              >
                <AttachmentManager
                  stepType="MOMENTS"
                  attachments={attachments}
                  mediaState={mediaState}
                  previewUrls={previewUrls}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  isTemplateActive={false}
                  readOnly={isViewMode}
                  validateUrl={validateUrl}
                  handleFileUpload={handleFileUpload}
                  handleRemoveFile={handleRemoveFile}
                  handleUpdatePreviewUrls={(newUrls) => {
                    setPreviewUrls(prev => ({ ...prev, ...newUrls }));
                  }}
                  onAddAttachment={(type) => {
                    const newAttachment = {
                      id: `${type.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                      type: type
                    };

                    // 根据类型初始化不同的字段
                    switch (type) {
                      case 'MOMENT_TEXT':
                        newAttachment.content = '';
                        break;
                      case 'MOMENT_IMAGE_GROUP':
                        newAttachment.imageGroup = { mediaIds: [], ossUrls: {} };
                        break;
                      case 'MOMENT_VIDEO':
                        newAttachment.mediaId = '';
                        newAttachment.ossUrl = '';
                        newAttachment.videoTitle = '';
                        newAttachment.videoDescription = '';
                        break;
                      case 'MOMENT_LINK':
                        newAttachment.title = '';
                        newAttachment.description = '';
                        newAttachment.url = '';
                        newAttachment.picUrl = '';
                        break;
                      case 'MINIPROGRAM':
                        newAttachment.title = '';
                        newAttachment.appid = '';
                        newAttachment.pagepath = '';
                        newAttachment.thumbMediaId = '';
                        newAttachment.thumbMediaId_ossUrl = '';
                        break;
                      default:
                        newAttachment.content = '';
                    }

                    setAttachments(prevAttachments => [...prevAttachments, newAttachment]);

                    // 返回新附件的ID，供素材选择逻辑使用
                    return newAttachment.id;
                  }}
                  onRemoveAttachment={(attachmentId) => {
                    setAttachments(prevAttachments =>
                      prevAttachments.filter(att => att.id !== attachmentId)
                    );
                  }}
                  onUpdateContent={(attachmentId, field, value) => {
                    console.log('🔄 MomentMassSend - onUpdateContent 被调用:', {
                      attachmentId,
                      field,
                      value,
                      valueType: typeof value
                    });

                    // 使用函数式更新避免状态竞争
                    setAttachments(prevAttachments => {
                      const newAttachments = prevAttachments.map(att =>
                        att.id === attachmentId ? { ...att, [field]: value } : att
                      );

                      console.log('📋 更新后的attachments:', newAttachments.find(att => att.id === attachmentId));

                      // 在状态更新完成后，更新mediaState
                      const currentAttachment = newAttachments.find(att => att.id === attachmentId);
                      const attachmentType = currentAttachment?.type;

                      // 同时更新mediaState以确保AttachmentManager正常工作（朋友圈类型）
                      if (field === 'mediaId') {
                        setMediaState(prev => {
                          const newState = { ...prev };
                          newState.momentMediaIds = {
                            ...prev.momentMediaIds,
                            [attachmentId]: value
                          };

                          if (attachmentType === 'MOMENT_IMAGE') {
                            newState.momentImageIds = {
                              ...prev.momentImageIds,
                              [attachmentId]: value
                            };
                          } else if (attachmentType === 'MOMENT_VIDEO') {
                            newState.momentVideoIds = {
                              ...prev.momentVideoIds,
                              [attachmentId]: value
                            };
                          }

                          return newState;
                        });
                      }

                      // 处理ossUrl字段 - 包括小程序缩略图的特殊处理
                      if (field === 'ossUrl' || field === 'mediaId_ossUrl' || field === 'thumbMediaId_ossUrl') {
                        console.log('🖼️ 处理OSS URL字段:', { field, value, attachmentType });

                        setMediaState(prev => {
                          return {
                            ...prev,
                            momentOssUrls: {
                              ...prev.momentOssUrls,
                              [attachmentId]: value
                            }
                          };
                        });

                        // 如果是小程序缩略图，同时更新previewUrls以便立即预览
                        if (field === 'thumbMediaId_ossUrl' && attachmentType === 'MINIPROGRAM' && value) {
                          const thumbMediaId = currentAttachment?.thumbMediaId;
                          if (thumbMediaId) {
                            console.log('🔄 更新小程序缩略图预览URL:', { thumbMediaId, value });
                            setPreviewUrls(prev => ({ ...prev, [thumbMediaId]: value }));
                          }
                        }
                      }

                      // 处理imageGroup字段
                      if (field === 'imageGroup' && attachmentType === 'MOMENT_IMAGE_GROUP') {
                        setMediaState(prev => {
                          return {
                            ...prev,
                            momentImageGroupIds: {
                              ...prev.momentImageGroupIds,
                              [attachmentId]: value
                            }
                          };
                        });
                      }

                      return newAttachments;
                    });
                  }}
                  onUpdateType={(attachmentId, newType) => {
                    setAttachments(prevAttachments => prevAttachments.map(att => {
                      if (att.id === attachmentId) {
                        // 重置附件，保留id和type，清空其他字段
                        const resetAttachment = { id: att.id, type: newType };

                        // 根据新类型初始化字段
                        switch (newType) {
                          case 'MOMENT_TEXT':
                            resetAttachment.content = '';
                            break;
                          case 'MOMENT_IMAGE_GROUP':
                            resetAttachment.imageGroup = { mediaIds: [], ossUrls: {} };
                            break;
                          case 'MOMENT_VIDEO':
                            resetAttachment.mediaId = '';
                            resetAttachment.ossUrl = '';
                            resetAttachment.videoTitle = '';
                            resetAttachment.videoDescription = '';
                            break;
                          case 'MOMENT_LINK':
                            resetAttachment.title = '';
                            resetAttachment.description = '';
                            resetAttachment.url = '';
                            resetAttachment.picUrl = '';
                            break;
                          case 'MINIPROGRAM':
                            resetAttachment.title = '';
                            resetAttachment.appid = '';
                            resetAttachment.pagepath = '';
                            resetAttachment.thumbMediaId = '';
                            resetAttachment.thumbMediaId_ossUrl = '';
                            break;
                          default:
                            resetAttachment.content = '';
                        }

                        return resetAttachment;
                      }
                      return att;
                    }));
                  }}
                />
              </Form.Item>

              <Divider />

              {/* 发布时间 */}
              <Form.Item
                name="scheduleType"
                label={<>发布时间</>}
                rules={[{ required: true, message: '请选择发布时间' }]}
              >
                <Radio.Group disabled={isViewMode}>
                  <Radio value="IMMEDIATE">立即发布</Radio>
                  <Radio value="SCHEDULED">定时发布</Radio>
                </Radio.Group>
              </Form.Item>

              {/* 定时发布时间 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.scheduleType !== currentValues.scheduleType
                }
              >
                {({ getFieldValue }) => {
                  const scheduleType = getFieldValue('scheduleType');

                  if (scheduleType === 'SCHEDULED') {
                    return (
                      <Form.Item
                        name="scheduleDateTime"
                        label="发布时间"
                        rules={[
                          { required: true, message: '请选择发布时间' },
                          {
                            validator: (_, value) => {
                              if (value && value.isBefore(dayjs())) {
                                return Promise.reject(new Error('发布时间不能早于当前时间'));
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime={{ format: 'HH:mm' }}
                          format="YYYY-MM-DD HH:mm"
                          placeholder="请选择发布时间"
                          style={{ width: '100%' }}
                          disabled={isViewMode}
                          disabledDate={(current) => current && current < dayjs().startOf('day')}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* 右侧预览区域 */}
        <Col span={10}>
          <MessagePreview
            step={{
              type: 'MOMENTS',
              actionType: 'SEND_MOMENT',
              momentsList: (() => {
                // 将attachments转换为momentsList格式
                const currentAttachments = attachments || [];
                const momentsList = [];

                // 处理每个附件
                currentAttachments.forEach(attachment => {
                  if (attachment.type === 'MOMENT_TEXT') {
                    momentsList.push({
                      type: 'MOMENT_TEXT',
                      params: {
                        content: attachment.content || ''
                      }
                    });
                  } else if (attachment.type === 'MOMENT_IMAGE') {
                    momentsList.push({
                      type: 'MOMENT_IMAGE',
                      params: {
                        mediaId: attachment.mediaId || '',
                        ossUrl: attachment.ossUrl || ''
                      }
                    });
                  } else if (attachment.type === 'MOMENT_IMAGE_GROUP') {
                    momentsList.push({
                      type: 'MOMENT_IMAGE_GROUP',
                      params: {
                        imageGroup: attachment.imageGroup || { mediaIds: [], ossUrls: {} }
                      }
                    });
                  } else if (attachment.type === 'MOMENT_VIDEO') {
                    momentsList.push({
                      type: 'MOMENT_VIDEO',
                      params: {
                        mediaId: attachment.mediaId || '',
                        ossUrl: attachment.ossUrl || ''
                      }
                    });
                  } else if (attachment.type === 'MOMENT_LINK') {
                    momentsList.push({
                      type: 'MOMENT_LINK',
                      params: {
                        title: attachment.title || '',
                        url: attachment.url || '',
                        description: attachment.description || '',
                        picUrl: attachment.picUrl || '',
                        picMediaId: attachment.picMediaId || '', // 传递封面的mediaId
                        picUrl_ossUrl: attachment.picUrl_ossUrl || ''
                      }
                    });
                  } else if (attachment.type === 'MINIPROGRAM') {
                    const miniprogramData = {
                      type: 'MINIPROGRAM',
                      params: {
                        title: attachment.title || '',
                        appid: attachment.appid || '',
                        pagepath: attachment.pagepath || '',
                        thumbMediaId: attachment.thumbMediaId || '',
                        thumbMediaId_ossUrl: attachment.thumbMediaId_ossUrl || ''
                      }
                    };

                    console.log('📤 MomentMassSend 传递给 MessagePreview 的小程序数据:', {
                      originalAttachment: attachment,
                      processedData: miniprogramData,
                      hasThumbMediaId: !!attachment.thumbMediaId,
                      hasThumbUrl: !!attachment.thumbMediaId_ossUrl
                    });

                    momentsList.push(miniprogramData);
                  }
                });

                // 如果没有内容，显示默认提示
                if (momentsList.length === 0) {
                  momentsList.push({
                    type: 'MOMENT_TEXT',
                    params: {
                      content: '请添加朋友圈内容...'
                    }
                  });
                }

                return momentsList;
              })()
            }}
            noTruncate={true}
          />
        </Col>
      </Row>
      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      {!isViewMode && (
        <div style={fixedFooterStyle}>
          <Space>
            <Button size="middle" onClick={() => navigate('/moment-mass-send')}>
              取消
            </Button>
            <Button
              type="primary"
              loading={submitLoading}
              onClick={handleSubmit}
              size="middle"
            >
              {isCopyMode ? '创建副本' : '提交群发'}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

export default MomentMassSend; 